import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function isVisible(field, values) {
  const vif = field.visible_if;
  if (!vif || !vif.field) return true;
  const ctrl = values[vif.field];
  if (vif.equals !== undefined && vif.equals !== null && vif.equals !== "") {
    return String(ctrl) === String(vif.equals);
  }
  const list = vif.in_ || vif.in;
  if (list && list.length) return list.map(String).includes(String(ctrl));
  return true;
}

function renderInput(field, value, onChange) {
  const common = { value: value ?? "", onChange: (e) => onChange(e.target.value), placeholder: field.placeholder || "" };
  if (field.type === "textarea") return <Textarea rows={3} {...common} />;
  if (field.type === "number") return <Input type="number" {...common} />;
  if (field.type === "date") return <Input type="date" {...common} />;
  if (field.type === "datetime") return <Input type="datetime-local" {...common} />;
  if (field.type === "email") return <Input type="email" {...common} />;
  if (field.type === "url") return <Input type="url" {...common} />;
  if (field.type === "toggle") return <Switch checked={!!value} onCheckedChange={onChange} />;
  if (field.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-1.5">
        {(field.options || []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={arr.includes(o)}
              onChange={(e) => onChange(e.target.checked ? arr.concat([o]) : arr.filter((x) => x !== o))} />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div className="space-y-1.5">
        {(field.options || []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input type="radio" checked={value === o} onChange={() => onChange(o)} />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "select") return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
      <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return <Input {...common} />;
}

export default function FormRunner() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/forms/${slug}`); setForm(r.data); }
      catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Form unavailable"); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  const visibleFields = useMemo(
    () => (form?.fields || []).filter((f) => isVisible(f, values)),
    [form, values]
  );

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/forms/${slug}/submit`, values);
      setDone(r.data.message || "Submitted!");
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading form…</div>;
  if (!form) return <div className="p-8 text-sm">Form not available.</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid={`form-page-${slug}`}>
      <div>
        <div className="overline text-muted-foreground mb-2">Form</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">{form.name}</h1>
        {form.description && <p className="text-sm text-muted-foreground mt-2">{form.description}</p>}
      </div>
      {done ? (
        <Card className="p-8 text-center space-y-4">
          <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-500" />
          <div className="font-heading text-lg">{done}</div>
          <Button variant="outline" onClick={() => { setDone(null); setValues({}); }}>Submit another</Button>
        </Card>
      ) : (
        <Card className="p-6 space-y-4">
          {visibleFields.map((f) => (
            <div key={f.key} data-testid={`form-runner-field-${f.key}`}>
              <Label className="text-xs">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
              {renderInput(f, values[f.key], (v) => setValues(Object.assign({}, values, { [f.key]: v })))}
              {f.help_text && <p className="text-[11px] text-muted-foreground mt-1">{f.help_text}</p>}
            </div>
          ))}
          <div className="pt-2">
            <Button onClick={submit} disabled={submitting} data-testid="form-submit">
              {submitting ? "Submitting…" : (form.submit_label || "Submit")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
